export interface LoadElement {
  (): Promise<boolean>;
}

let isLoading = false;
const loadingElements: Array<LoadElement> = [];

function getLoadElement(node: Node, targetLoadElement: HTMLElement): LoadElement | undefined {
  if (node.nodeType === Node.TEXT_NODE) {
    return;
  }
  return function () {
    targetLoadElement.appendChild(node);
    return Promise.resolve(true);
  };
}

function getLoadScript(origin: HTMLScriptElement, targetLoadElement: HTMLElement): LoadElement {
  return function () {
    return new Promise((resolve) => {
      const script = document.createElement('script');

      script.onload = () => {
        resolve(true);
      };
      script.onerror = () => {
        resolve(false);
      };
      for (const attr of Object.values(origin.attributes)) {
        script.setAttribute(attr.name, attr.value);
      }

      script.async ??= false;

      if (origin.textContent) {
        script.textContent = origin.textContent;
      }

      if (origin.async || origin.defer || !script.src) {
        resolve(true);
      }

      targetLoadElement.append(script);
    });
  };
}

function isScriptElement(node: Node): node is HTMLScriptElement {
  return node.nodeType === Node.ELEMENT_NODE && node.nodeName.toLocaleLowerCase() === 'script';
}

function createElement(source: Node, target: HTMLElement) {
  let loadingElement: LoadElement | undefined;

  if (isScriptElement(source)) {
    loadingElement = getLoadScript(source, target);
  } else {
    loadingElement = getLoadElement(source, target);
  }

  if (!loadingElement) {
    return;
  }

  loadingElements.push(loadingElement);
  loadElement();
}

async function loadElement() {
  if (isLoading) {
    return;
  }

  isLoading = true;

  while (loadingElements.length) {
    const promise = loadingElements.shift();
    if (promise) {
      await promise();
    }
  }
  isLoading = false;
}

function loadScript(code: string) {
  const template = document.createElement('template');
  template.innerHTML = code;

  for (const child of Array.from(template.content.childNodes)) {
    createElement(child, document.head);
  }
}

export default loadScript;
